"use client";

import { revalidateLogic, useForm } from "@tanstack/react-form";
import { Check, MapPin } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { useMemo, useState } from "react";
import { z } from "zod";
import {
  type BookingContactDetails,
  type BookingDetailsSource,
  saveBookingContactDetails,
} from "@/actions/booking-contact-details";
import {
  EMPTY_MEDIA_UPLOAD_STATE,
  ImageUpload,
  type MediaUploadState,
} from "@/components/image-upload";
import { LocationSearchBox } from "@/components/location-search-box";
import {
  OnboardingLabel,
  OnboardingPrimaryButton,
} from "@/components/onboarding/onboarding-chrome";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { FieldError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { GENDER, type Gender } from "@/db/schema/profile";
import { ASPECT_RATIO_VALUE } from "@/lib/aspect-ratios";
import {
  type BookingFieldKey,
  type BookingFieldModes,
  missingRequiredBookingFields,
  unansweredBookingFields,
} from "@/lib/booking-fields";
import { parseContactEmail } from "@/lib/identity/contact-email";
import { isPlaceholderDisplayName } from "@/lib/identity/display-name";
import { sanitizeLocalityPlace } from "@/lib/location-display";
import { type MediaAssetSlim, toMediaAssetSlimForWrite } from "@/lib/media";
import { cn, getAge } from "@/lib/utils";

const GENDER_LABEL: Record<Gender, string> = {
  female: "Female",
  male: "Male",
  other: "Other",
};

/** The onboarding step-1 field, so checkout reads as the same product. */
const FIELD_CLASS =
  "h-[52px] w-full rounded-2xl border border-border/60 bg-surface/55 px-4 font-semibold text-[17px] text-text-primary backdrop-blur-xl backdrop-saturate-150 transition-[border-color,box-shadow] duration-200 placeholder:font-medium placeholder:text-text-tertiary focus-visible:border-brand/60 focus-visible:ring-4 focus-visible:ring-brand/20 md:text-lg dark:bg-white/5";

/**
 * The same surface for the location search input. It keeps the base input's
 * left padding so the search glyph has room, and only overrides the size and
 * weight that would otherwise read as a different form.
 */
const LOCATION_INPUT_CLASS =
  "h-[52px] rounded-2xl font-semibold text-[17px] backdrop-blur-xl backdrop-saturate-150 md:text-lg";

type FormValues = {
  name: string;
  email: string;
  gender: Gender | "";
  dateOfBirth: string;
  photo: MediaAssetSlim | null;
  location: BookingLocation | null;
};

/** The locality a person picked, already trimmed to a place name. */
type BookingLocation = {
  displayName: string;
  latitude: number;
  longitude: number;
};

type ServerErrorField = keyof FormValues | "form";

/** Which inputs this person sees: only what is missing from their account. */
export function bookingDetailsToAsk(
  details: BookingContactDetails,
  modes: BookingFieldModes,
): { name: boolean; email: boolean; extras: BookingFieldKey[] } {
  return {
    name: !details.name,
    email: !details.email,
    extras: unansweredBookingFields(modes, details),
  };
}

/** True when checkout has anything to ask before it can carry on. */
export function bookingDetailsIncomplete(
  details: BookingContactDetails,
  modes: BookingFieldModes,
) {
  const ask = bookingDetailsToAsk(details, modes);
  return ask.name || ask.email || ask.extras.length > 0;
}

/** True when something checkout cannot go ahead without is missing. */
export function bookingDetailsRequiredMissing(
  details: BookingContactDetails,
  modes: BookingFieldModes,
) {
  return (
    !details.name ||
    !details.email ||
    missingRequiredBookingFields(modes, details).length > 0
  );
}

const nameSchema = z
  .string()
  .trim()
  .min(1, { message: "Enter your full name", abort: true })
  .max(100, "Name must be under 100 characters")
  .refine((value) => !isPlaceholderDisplayName(value), "Enter your real name");

const emailSchema = z
  .string()
  .trim()
  .min(1, { message: "Enter your email", abort: true })
  .refine((value) => parseContactEmail(value).ok, "Enter a valid email");

const dateOfBirthSchema = z.string().refine((date) => {
  if (!date) return true;
  const age = getAge(date);
  return age !== null && age >= 13;
}, "You must be at least 13 years old");

function buildSchema(
  ask: ReturnType<typeof bookingDetailsToAsk>,
  modes: BookingFieldModes,
) {
  const required = (key: BookingFieldKey) =>
    ask.extras.includes(key) && modes[key] === "required";
  return z.object({
    name: ask.name ? nameSchema : z.string(),
    email: ask.email ? emailSchema : z.string(),
    gender: required("gender")
      ? z.enum(GENDER, { message: "Choose one" })
      : z.union([z.enum(GENDER), z.literal("")]),
    dateOfBirth: required("dateOfBirth")
      ? z
          .string()
          .min(1, { message: "Pick your date of birth", abort: true })
          .pipe(dateOfBirthSchema)
      : dateOfBirthSchema,
    photo: required("profilePhoto")
      ? z.custom<MediaAssetSlim | null>((value) => value !== null, {
          message: "Add a photo",
        })
      : z.custom<MediaAssetSlim | null>(() => true),
    location: required("location")
      ? z.custom<BookingLocation | null>(
          (value) => {
            if (!value || typeof value !== "object") return false;
            const displayName = (value as BookingLocation).displayName;
            return (
              typeof displayName === "string" && displayName.trim().length > 0
            );
          },
          { message: "Search and select your location" },
        )
      : z.custom<BookingLocation | null>(() => true),
  });
}

function firstMessage(
  errors: unknown[],
): Array<{ message: string }> | undefined {
  const error = errors[0];
  if (!error) return undefined;
  if (typeof error === "string") return [{ message: error }];
  if (typeof error === "object" && "message" in error) {
    return [{ message: String((error as { message: unknown }).message) }];
  }
  return undefined;
}

type BookingDetailsDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** What is already on file, read when checkout decided to ask. */
  details: BookingContactDetails;
  modes: BookingFieldModes;
  /** Saved and nothing required is missing — carry on with checkout. */
  onComplete: (details: BookingContactDetails) => void;
  context: BookingDetailsSource;
};

/**
 * The details a booking needs, asked once, in one place, right before
 * checkout: name and email always, plus whatever the organiser switched on in
 * the event's booking details. Only the gaps are shown, so a person with a
 * complete profile never sees it.
 *
 * Validation follows onboarding step 1: nothing is flagged until the first
 * Continue, and after that every field re-checks as it changes, so a fixed
 * field's error disappears immediately instead of waiting for another tap.
 *
 * Mounted fresh per opening (callers key it), so the fields asked never
 * change under somebody mid-edit.
 */
export function BookingDetailsDrawer({
  open,
  onOpenChange,
  details,
  modes,
  onComplete,
  context,
}: BookingDetailsDrawerProps) {
  const reduceMotion = useReducedMotion();
  const ask = useMemo(
    () => bookingDetailsToAsk(details, modes),
    [details, modes],
  );
  const schema = useMemo(() => buildSchema(ask, modes), [ask, modes]);
  const [serverErrors, setServerErrors] = useState<
    Partial<Record<ServerErrorField, string>>
  >({});
  const [photoUpload, setPhotoUpload] = useState<MediaUploadState>(
    EMPTY_MEDIA_UPLOAD_STATE,
  );

  const clearServerError = (field: ServerErrorField) =>
    setServerErrors((current) =>
      current[field] || current.form
        ? { ...current, [field]: undefined, form: undefined }
        : current,
    );

  const form = useForm({
    defaultValues: {
      name: "",
      email: "",
      gender: "",
      dateOfBirth: "",
      photo: null,
      location: null,
    } as FormValues,
    // Must stay `onDynamic`-only — see onboarding step 1: an `onBlur`
    // validator pins errors on controls that never blur (the date picker, the
    // gender chips, the photo tile).
    validationLogic: revalidateLogic({
      mode: "submit",
      modeAfterSubmission: "change",
    }),
    validators: { onDynamic: schema },
    onSubmit: async ({ value }) => {
      const result = await saveBookingContactDetails({
        ...(ask.name ? { name: value.name } : {}),
        ...(ask.email ? { email: value.email } : {}),
        ...(ask.extras.includes("gender") && value.gender
          ? { gender: value.gender }
          : {}),
        ...(ask.extras.includes("dateOfBirth") && value.dateOfBirth
          ? { dateOfBirth: value.dateOfBirth }
          : {}),
        ...(ask.extras.includes("profilePhoto") && value.photo
          ? { profilePhoto: value.photo }
          : {}),
        ...(ask.extras.includes("location") && value.location
          ? { location: value.location }
          : {}),
        context,
      });
      if (!result.ok) {
        const field: ServerErrorField =
          result.code === "INVALID_NAME"
            ? "name"
            : result.code === "INVALID_EMAIL" ||
                result.code === "VERIFIED_EMAIL_EXISTS"
              ? "email"
              : result.code === "INVALID_GENDER"
                ? "gender"
                : result.code === "INVALID_DATE_OF_BIRTH"
                  ? "dateOfBirth"
                  : result.code === "INVALID_PHOTO"
                    ? "photo"
                    : result.code === "INVALID_LOCATION"
                      ? "location"
                      : "form";
        setServerErrors({ [field]: result.message });
        return;
      }
      onComplete(result.data);
    },
  });

  const optional = (key: BookingFieldKey) => modes[key] === "optional";

  return (
    <form.Subscribe selector={(state) => state.isSubmitting}>
      {(isSubmitting) => (
        <Drawer
          open={open}
          onOpenChange={onOpenChange}
          dismissible={!isSubmitting}
        >
          <DrawerContent className="mx-auto max-h-[90dvh] w-full max-w-[540px] overflow-hidden border-border bg-surface data-[vaul-drawer-direction=bottom]:rounded-t-[30px] dark:border-white/10 dark:bg-[#0d0c13]">
            <DrawerHeader className="shrink-0 px-5 pt-3 pb-1 text-left!">
              <DrawerTitle className="text-[26px] leading-[1.15] font-bold tracking-tight text-balance text-text-primary">
                Your details
              </DrawerTitle>
            </DrawerHeader>

            {/* No `onb-scroll-fade` here: its mask is a stacking context, which
                traps the birthday picker's fixed overlay under the footer. */}
            <form
              id="booking-details-form"
              className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 pt-4 pb-5"
              onSubmit={(event) => {
                event.preventDefault();
                event.stopPropagation();
                void form.handleSubmit();
              }}
            >
              {ask.name ? (
                <form.Field name="name">
                  {(field) => (
                    <div className="space-y-2">
                      <OnboardingLabel>
                        <label htmlFor="booking-details-name">Full name</label>
                      </OnboardingLabel>
                      <div className="relative">
                        <Input
                          id="booking-details-name"
                          name="name"
                          autoComplete="name"
                          placeholder="First and last name"
                          value={field.state.value}
                          aria-invalid={
                            field.state.meta.errors.length > 0 ||
                            Boolean(serverErrors.name)
                          }
                          onBlur={field.handleBlur}
                          onChange={(event) => {
                            field.handleChange(event.target.value);
                            clearServerError("name");
                          }}
                          className={cn(FIELD_CLASS, "pr-11")}
                        />
                        <ValidTick
                          show={
                            field.state.value.trim().length > 0 &&
                            nameSchema.safeParse(field.state.value).success
                          }
                        />
                      </div>
                      <FieldError
                        className="pl-1"
                        errors={
                          serverErrors.name
                            ? [{ message: serverErrors.name }]
                            : firstMessage(field.state.meta.errors)
                        }
                      />
                    </div>
                  )}
                </form.Field>
              ) : null}

              {ask.email ? (
                <form.Field name="email">
                  {(field) => (
                    <div className="space-y-2">
                      <OnboardingLabel>
                        <label htmlFor="booking-details-email">Email</label>
                      </OnboardingLabel>
                      <div className="relative">
                        <Input
                          id="booking-details-email"
                          name="email"
                          type="email"
                          inputMode="email"
                          autoComplete="email"
                          autoCapitalize="none"
                          spellCheck={false}
                          placeholder="you@example.com"
                          value={field.state.value}
                          aria-invalid={
                            field.state.meta.errors.length > 0 ||
                            Boolean(serverErrors.email)
                          }
                          onBlur={field.handleBlur}
                          onChange={(event) => {
                            field.handleChange(event.target.value);
                            clearServerError("email");
                          }}
                          className={cn(FIELD_CLASS, "pr-11")}
                        />
                        <ValidTick
                          show={
                            emailSchema.safeParse(field.state.value).success
                          }
                        />
                      </div>
                      <FieldError
                        className="pl-1"
                        errors={
                          serverErrors.email
                            ? [{ message: serverErrors.email }]
                            : firstMessage(field.state.meta.errors)
                        }
                      />
                    </div>
                  )}
                </form.Field>
              ) : null}

              {ask.extras.includes("gender") ? (
                <form.Field name="gender">
                  {(field) => (
                    <div className="space-y-2">
                      <OnboardingLabel optional={optional("gender")}>
                        I am
                      </OnboardingLabel>
                      <fieldset className="grid grid-cols-3 gap-2">
                        <legend className="sr-only">Gender</legend>
                        {GENDER.map((option) => {
                          const selected = field.state.value === option;
                          return (
                            <button
                              key={option}
                              type="button"
                              aria-pressed={selected}
                              onClick={() => {
                                // Tapping the chosen chip again clears an
                                // optional answer; a required one stays put.
                                field.handleChange(
                                  selected && optional("gender") ? "" : option,
                                );
                                clearServerError("gender");
                              }}
                              className={cn(
                                "relative flex h-[52px] items-center justify-center rounded-2xl border text-[15px] font-semibold backdrop-blur-xl transition-[transform,border-color,color] duration-150 ease-out-strong active:scale-95",
                                selected
                                  ? "border-transparent text-white"
                                  : "border-border/60 bg-surface/55 text-text-secondary dark:bg-white/5",
                              )}
                            >
                              {selected ? (
                                <motion.span
                                  layoutId={
                                    reduceMotion
                                      ? undefined
                                      : "booking-details-gender-pill"
                                  }
                                  transition={{
                                    type: "spring",
                                    stiffness: 420,
                                    damping: 32,
                                  }}
                                  className="absolute inset-0 rounded-2xl bg-brand dark:bg-[#7c4dff]"
                                />
                              ) : null}
                              <span className="relative">
                                {GENDER_LABEL[option]}
                              </span>
                            </button>
                          );
                        })}
                      </fieldset>
                      <FieldError
                        className="pl-1"
                        errors={
                          serverErrors.gender
                            ? [{ message: serverErrors.gender }]
                            : firstMessage(field.state.meta.errors)
                        }
                      />
                    </div>
                  )}
                </form.Field>
              ) : null}

              {ask.extras.includes("dateOfBirth") ? (
                <form.Field name="dateOfBirth">
                  {(field) => (
                    <div className="space-y-2">
                      <OnboardingLabel optional={optional("dateOfBirth")}>
                        Birthday
                      </OnboardingLabel>
                      <DatePicker
                        id="booking-details-date-of-birth"
                        name="dateOfBirth"
                        value={field.state.value}
                        onValueChange={(value) => {
                          field.handleChange(value);
                          clearServerError("dateOfBirth");
                        }}
                        placeholder="Pick your date of birth"
                        variant="onboarding"
                      />
                      <FieldError
                        className="pl-1"
                        errors={
                          serverErrors.dateOfBirth
                            ? [{ message: serverErrors.dateOfBirth }]
                            : firstMessage(field.state.meta.errors)
                        }
                      />
                    </div>
                  )}
                </form.Field>
              ) : null}

              {ask.extras.includes("profilePhoto") ? (
                <form.Field name="photo">
                  {(field) => (
                    <div className="space-y-2">
                      <OnboardingLabel optional={optional("profilePhoto")}>
                        Profile photo
                      </OnboardingLabel>
                      <ImageUpload
                        maxImages={1}
                        acceptVideos={false}
                        showReorderHint={false}
                        showCountHeading={false}
                        aspect={ASPECT_RATIO_VALUE.square}
                        enableCropping
                        uploadScope={{ domain: "profiles", slot: "images" }}
                        onUploadStateChange={setPhotoUpload}
                        onImagesChange={(images) => {
                          field.handleChange(
                            images[0]
                              ? toMediaAssetSlimForWrite(images[0], 0)
                              : null,
                          );
                          clearServerError("photo");
                        }}
                      />
                      <FieldError
                        className="pl-1"
                        errors={
                          serverErrors.photo
                            ? [{ message: serverErrors.photo }]
                            : firstMessage(field.state.meta.errors)
                        }
                      />
                    </div>
                  )}
                </form.Field>
              ) : null}

              {ask.extras.includes("location") ? (
                <form.Field name="location">
                  {(field) => (
                    <div className="space-y-2">
                      <OnboardingLabel optional={optional("location")}>
                        Location
                      </OnboardingLabel>
                      {field.state.value ? (
                        <SelectedLocation
                          displayName={field.state.value.displayName}
                          onClear={() => {
                            field.handleChange(null);
                            clearServerError("location");
                          }}
                        />
                      ) : (
                        <LocationSearchBox
                          onSelect={(place) => {
                            const sanitized = sanitizeLocalityPlace(place);
                            field.handleChange({
                              displayName: sanitized.display_name,
                              latitude: Number.parseFloat(sanitized.lat),
                              longitude: Number.parseFloat(sanitized.lon),
                            });
                            clearServerError("location");
                          }}
                          placeholder="Search your area..."
                          inputClassName={LOCATION_INPUT_CLASS}
                          searchProfile="locality"
                        />
                      )}
                      <FieldError
                        className="pl-1"
                        errors={
                          serverErrors.location
                            ? [{ message: serverErrors.location }]
                            : firstMessage(field.state.meta.errors)
                        }
                      />
                    </div>
                  )}
                </form.Field>
              ) : null}
            </form>

            <div className="shrink-0 px-5 pt-3 pb-[max(env(safe-area-inset-bottom),20px)]">
              {serverErrors.form ? (
                <p role="alert" className="mb-3 text-center text-sm text-error">
                  {serverErrors.form}
                </p>
              ) : photoUpload.isUploading ? (
                <p className="mb-3 text-center text-sm text-text-secondary">
                  Uploading your photo…
                </p>
              ) : null}
              <form.Subscribe selector={(state) => state.canSubmit}>
                {(canSubmit) => (
                  <div className="flex">
                    <OnboardingPrimaryButton
                      type="submit"
                      form="booking-details-form"
                      disabled={
                        !canSubmit ||
                        isSubmitting ||
                        photoUpload.isUploading ||
                        photoUpload.hasErrors
                      }
                      busy={isSubmitting}
                    >
                      {isSubmitting ? "Saving" : "Continue"}
                    </OnboardingPrimaryButton>
                  </div>
                )}
              </form.Subscribe>
            </div>
          </DrawerContent>
        </Drawer>
      )}
    </form.Subscribe>
  );
}

function ValidTick({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <span className="pointer-events-none absolute top-1/2 right-4 flex size-6 -translate-y-1/2 items-center justify-center rounded-full bg-success">
      <Check className="size-3.5 text-white" strokeWidth={3} />
    </span>
  );
}

/** The chosen locality, in the same shape as the field it replaces. */
function SelectedLocation({
  displayName,
  onClear,
}: {
  displayName: string;
  onClear: () => void;
}) {
  return (
    <div className="flex h-[52px] items-center gap-3 rounded-2xl border border-border/60 bg-surface/55 pr-2 pl-4 backdrop-blur-xl backdrop-saturate-150 dark:bg-white/5">
      <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand/12 text-brand dark:text-[#a78bfa]">
        <MapPin className="size-4" />
      </span>
      <span className="min-w-0 flex-1 truncate text-[15px] font-semibold text-text-primary">
        {displayName}
      </span>
      <button
        type="button"
        onClick={onClear}
        className="shrink-0 rounded-full px-3 py-1.5 text-xs font-medium text-text-secondary transition-colors pointer-fine:hover:bg-surface pointer-fine:hover:text-text-primary"
      >
        Change
      </button>
    </div>
  );
}
